// Transfer Storage Utility
// This simulates API calls by storing transfers in localStorage
// In production, this would be replaced with actual API calls

import { StockTransfer } from "@/pages/stock-transfers";
import { TransferConfigData, TransferItem } from "@/pages/stock-transfers-step2";

const STORAGE_KEY = "stock_transfers";
const STORAGE_KEY_ITEMS = "stock_transfer_items";
const STORAGE_KEY_CONFIG = "stock_transfer_configs";

// Get all transfers from storage
export function getTransfers(): StockTransfer[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error("Error loading transfers:", error);
    return [];
  }
}

// Get transfer items for a specific transfer
export function getTransferItems(transferId: string): TransferItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ITEMS);
    if (!stored) return [];
    const allItems: Record<string, TransferItem[]> = JSON.parse(stored);
    return allItems[transferId] || [];
  } catch (error) {
    console.error("Error loading transfer items:", error);
    return [];
  }
}

// Save transfer items
function saveTransferItems(transferId: string, items: TransferItem[]) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ITEMS);
    const allItems: Record<string, TransferItem[]> = stored ? JSON.parse(stored) : {};
    allItems[transferId] = items;
    localStorage.setItem(STORAGE_KEY_ITEMS, JSON.stringify(allItems));
  } catch (error) {
    console.error("Error saving transfer items:", error);
  }
}

// Delete transfer items
function deleteTransferItems(transferId: string) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ITEMS);
    if (!stored) return;
    const allItems: Record<string, TransferItem[]> = JSON.parse(stored);
    delete allItems[transferId];
    localStorage.setItem(STORAGE_KEY_ITEMS, JSON.stringify(allItems));
  } catch (error) {
    console.error("Error deleting transfer items:", error);
  }
}

// Save transfer configuration (for drafts)
export function saveTransferConfig(transferId: string, configData: TransferConfigData) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_CONFIG);
    const allConfigs: Record<string, TransferConfigData> = stored ? JSON.parse(stored) : {};
    allConfigs[transferId] = configData;
    localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(allConfigs));
  } catch (error) {
    console.error("Error saving transfer config:", error);
  }
}

// Get transfer configuration
export function getTransferConfig(transferId: string): TransferConfigData | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_CONFIG);
    if (!stored) return null;
    const allConfigs: Record<string, TransferConfigData> = JSON.parse(stored);
    return allConfigs[transferId] || null;
  } catch (error) {
    console.error("Error loading transfer config:", error);
    return null;
  }
}

// Delete transfer configuration
function deleteTransferConfig(transferId: string) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_CONFIG);
    if (!stored) return;
    const allConfigs: Record<string, TransferConfigData> = JSON.parse(stored);
    delete allConfigs[transferId];
    localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(allConfigs));
  } catch (error) {
    console.error("Error deleting transfer config:", error);
  }
}

// Save a transfer
export function saveTransfer(
  configData: TransferConfigData,
  status: "draft" | "in-transit" | "completed",
  expectedArrivalDate?: string
): StockTransfer {
  const transfers = getTransfers();
  
  // Generate new ID
  const existingIds = transfers.map(t => parseInt(t.id.replace("TRF-", "")));
  const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
  const id = `TRF-${String(nextId).padStart(3, "0")}`;
  
  // Create new transfer
  const newTransfer: StockTransfer = {
    id,
    reference: configData.reference,
    status,
    createdAt: new Date().toISOString(),
    createdBy: "Current User", // In production, get from auth context
    dispatchedAt: status === "in-transit" ? new Date().toISOString() : undefined,
    expectedArrivalDate: status === "in-transit" ? expectedArrivalDate : undefined,
    completedAt: status === "completed" ? new Date().toISOString() : undefined,
    itemCount: configData.transfers.length,
    totalQuantity: configData.transfers.reduce((sum, t) => sum + t.quantity, 0),
    notes: configData.notes,
  };
  
  // Add to transfers
  transfers.push(newTransfer);
  
  // Save to storage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(transfers));
  
  // Save transfer items
  saveTransferItems(id, configData.transfers);
  
  // Save full config for drafts and in-transit (so they can be edited later)
  if (status === "draft" || status === "in-transit") {
    saveTransferConfig(id, configData);
  }
  
  // Dispatch custom event to notify other components
  window.dispatchEvent(new CustomEvent("transfersUpdated"));
  
  return newTransfer;
}

// Update transfer status only
export function updateTransferStatus(
  id: string,
  status: "draft" | "in-transit" | "completed" | "cancelled",
  expectedArrivalDate?: string
): StockTransfer | null {
  const transfers = getTransfers();
  const index = transfers.findIndex(t => t.id === id);
  
  if (index === -1) return null;
  
  // Update status
  transfers[index] = {
    ...transfers[index],
    status,
    dispatchedAt: status === "in-transit" && !transfers[index].dispatchedAt ? new Date().toISOString() : transfers[index].dispatchedAt,
    expectedArrivalDate: status === "in-transit" ? expectedArrivalDate : transfers[index].expectedArrivalDate,
    completedAt: status === "completed" ? new Date().toISOString() : transfers[index].completedAt,
  };
  
  // Save to storage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(transfers));
  
  // Dispatch custom event
  window.dispatchEvent(new CustomEvent("transfersUpdated"));
  
  return transfers[index];
}

// Update a transfer
export function updateTransfer(
  id: string,
  configData: TransferConfigData,
  status: "draft" | "in-transit" | "completed",
  expectedArrivalDate?: string
): StockTransfer | null {
  const transfers = getTransfers();
  const index = transfers.findIndex(t => t.id === id);
  
  if (index === -1) return null;
  
  // Update transfer
  const updatedTransfer: StockTransfer = {
    ...transfers[index],
    reference: configData.reference,
    status,
    itemCount: configData.transfers.length,
    totalQuantity: configData.transfers.reduce((sum, t) => sum + t.quantity, 0),
    notes: configData.notes,
    dispatchedAt: status === "in-transit" && !transfers[index].dispatchedAt ? new Date().toISOString() : transfers[index].dispatchedAt,
    expectedArrivalDate: status === "in-transit" ? expectedArrivalDate : transfers[index].expectedArrivalDate,
    completedAt: status === "completed" ? new Date().toISOString() : transfers[index].completedAt,
  };
  
  transfers[index] = updatedTransfer;
  
  // Save to storage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(transfers));
  
  // Update transfer items
  saveTransferItems(id, configData.transfers);
  
  // Dispatch custom event to notify other components
  window.dispatchEvent(new CustomEvent("transfersUpdated"));
  
  return updatedTransfer;
}

// Delete a transfer
export function deleteTransfer(id: string): boolean {
  const transfers = getTransfers();
  const filtered = transfers.filter(t => t.id !== id);
  
  if (filtered.length === transfers.length) return false;
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  
  // Delete associated transfer items
  deleteTransferItems(id);
  
  // Delete associated transfer config
  deleteTransferConfig(id);
  
  // Dispatch custom event to notify other components
  window.dispatchEvent(new CustomEvent("transfersUpdated"));
  
  return true;
}

// Duplicate a transfer
export function duplicateTransfer(id: string): StockTransfer | null {
  const transfers = getTransfers();
  const originalTransfer = transfers.find(t => t.id === id);
  
  if (!originalTransfer) return null;
  
  // Get the original transfer items and config
  const originalItems = getTransferItems(id);
  const originalConfig = getTransferConfig(id);
  
  if (!originalConfig) {
    console.error("Cannot duplicate transfer without configuration data");
    return null;
  }
  
  // Generate new ID
  const existingIds = transfers.map(t => parseInt(t.id.replace("TRF-", "")));
  const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
  const newId = `TRF-${String(nextId).padStart(3, "0")}`;
  
  // Generate new reference with "Copy" suffix
  const newReference = `${originalConfig.reference} (Copy)`;
  
  // Create new transfer as draft
  const newTransfer: StockTransfer = {
    id: newId,
    reference: newReference,
    status: "draft",
    createdAt: new Date().toISOString(),
    createdBy: "Current User", // In production, get from auth context
    itemCount: originalTransfer.itemCount,
    totalQuantity: originalTransfer.totalQuantity,
    notes: originalConfig.notes,
  };
  
  // Add to transfers
  transfers.push(newTransfer);
  
  // Save to storage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(transfers));
  
  // Save transfer items (duplicate the items)
  saveTransferItems(newId, originalItems);
  
  // Save config with updated reference
  const newConfig: TransferConfigData = {
    ...originalConfig,
    reference: newReference,
    date: new Date().toISOString().split('T')[0], // Set to today's date
  };
  saveTransferConfig(newId, newConfig);
  
  // Dispatch custom event to notify other components
  window.dispatchEvent(new CustomEvent("transfersUpdated"));
  
  return newTransfer;
}

// Force reinitialize - clears all data and creates fresh mock data
export function forceReinitialize() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_KEY_ITEMS);
  localStorage.removeItem(STORAGE_KEY_CONFIG);
  initializeMockData();
}

// Initialize with mock data if empty
export function initializeMockData() {
  const transfers = getTransfers();
  
  // Check if items exist
  const itemsStored = localStorage.getItem(STORAGE_KEY_ITEMS);
  
  // If transfers exist but items don't, we need to reinitialize everything
  if (transfers.length > 0 && !itemsStored) {
    forceReinitialize();
    return;
  }
  
  if (transfers.length === 0) {
    const mockTransfers: StockTransfer[] = [
      {
        id: "TRF-001",
        reference: "TRF-2024-001",
        status: "completed",
        createdAt: "2024-01-15T10:30:00",
        createdBy: "John Smith",
        completedAt: "2024-01-15T11:45:00",
        itemCount: 3,
        totalQuantity: 150,
        notes: "Monthly stock rebalancing",
      },
      {
        id: "TRF-002",
        reference: "TRF-2024-002",
        status: "in-transit",
        createdAt: "2024-01-16T09:15:00",
        createdBy: "Sarah Johnson",
        dispatchedAt: "2024-01-16T10:30:00",
        expectedArrivalDate: "2024-01-19",
        itemCount: 2,
        totalQuantity: 75,
        notes: "Transfer from Warehouse A to Warehouse B",
      },
      {
        id: "TRF-003",
        reference: "TRF-2024-003",
        status: "draft",
        createdAt: "2024-01-16T14:20:00",
        createdBy: "Mike Wilson",
        itemCount: 5,
        totalQuantity: 200,
        notes: "Urgent transfer for customer order",
      },
    ];
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mockTransfers));
    
    // Initialize mock transfer items
    const mockTransferItems: Record<string, TransferItem[]> = {
      "TRF-001": [
        {
          itemId: "1",
          itemCode: "STK001",
          itemName: "Laptop Dell XPS 15",
          availableQuantity: 45,
          sourceLocation: "WH-A - A1-R1-S1",
          destinationLocation: "WH-B - B1-R1-S1",
          quantity: 50,
        },
        {
          itemId: "2",
          itemCode: "STK002",
          itemName: "Office Chair Ergonomic",
          availableQuantity: 120,
          sourceLocation: "WH-A - A1-R1-S2",
          destinationLocation: "WH-B - B1-R1-S2",
          quantity: 75,
        },
        {
          itemId: "3",
          itemCode: "STK003",
          itemName: "A4 Paper Ream (500 sheets)",
          availableQuantity: 450,
          sourceLocation: "WH-A - A1-R2-S1",
          destinationLocation: "WH-C - C1-R1-S1",
          quantity: 25,
        },
      ],
      "TRF-002": [
        {
          itemId: "4",
          itemCode: "STK004",
          itemName: "Steel Rods 10mm x 6m",
          availableQuantity: 200,
          sourceLocation: "WH-B - B1-R1-S1",
          destinationLocation: "WH-A - A1-R1-S1",
          quantity: 50,
        },
        {
          itemId: "5",
          itemCode: "STK005",
          itemName: "Wireless Mouse Logitech",
          availableQuantity: 85,
          sourceLocation: "WH-C - C1-R1-S1",
          destinationLocation: "WH-A - A1-R1-S2",
          quantity: 25,
        },
      ],
      "TRF-003": [
        {
          itemId: "6",
          itemCode: "STK006",
          itemName: "Standing Desk Adjustable",
          availableQuantity: 35,
          sourceLocation: "WH-A - A1-R1-S1",
          destinationLocation: "WH-B - B1-R1-S1",
          quantity: 40,
        },
        {
          itemId: "7",
          itemCode: "STK007",
          itemName: "Ballpoint Pens Blue (Box of 50)",
          availableQuantity: 300,
          sourceLocation: "WH-B - B1-R1-S2",
          destinationLocation: "WH-C - C1-R1-S1",
          quantity: 60,
        },
        {
          itemId: "8",
          itemCode: "STK009",
          itemName: "Monitor 27\" 4K Dell",
          availableQuantity: 60,
          sourceLocation: "WH-A - A1-R2-S1",
          destinationLocation: "WH-B - B1-R2-S1",
          quantity: 30,
        },
        {
          itemId: "9",
          itemCode: "STK010",
          itemName: "Filing Cabinet 4-Drawer",
          availableQuantity: 40,
          sourceLocation: "WH-B - B1-R1-S1",
          destinationLocation: "WH-A - A1-R1-S1",
          quantity: 35,
        },
        {
          itemId: "10",
          itemCode: "STK011",
          itemName: "Stapler Heavy Duty",
          availableQuantity: 95,
          sourceLocation: "WH-C - C1-R1-S1",
          destinationLocation: "WH-A - A1-R1-S2",
          quantity: 35,
        },
      ],
    };
    
    localStorage.setItem(STORAGE_KEY_ITEMS, JSON.stringify(mockTransferItems));
  }
}
